import gql from 'graphql-tag';

/**
 * Query to search published posts by search term.
 *
 * @param limit - Maximum number of posts to return (optional)
 * @param skip - Number of posts to skip for pagination (optional)
 * @param search - Search term to filter posts (optional)
 * @returns List of published posts matching the search criteria with images, authors, and categories
 * @note Reserved for future implementation
 */
export const SEARCH_POSTS = gql`
  query search($limit: Int, $skip: Int, $search: String) {
    publishedPosts(limit: $limit, skip: $skip, search: $search) {
      postId
      title
      slug
      type
      description
      publishedAt
      modifiedAt
      image {
        sourceUrl
        sourceSet
      }
      authors {
        displayName
        preferredUsername
      }
      categories {
        name
        slug
      }
    }
  }
`;

/**
 * Query to search both posts and users by a search term.
 *
 * @param limit - Maximum number of posts to return (optional)
 * @param skip - Number of posts to skip for pagination (optional)
 * @param search - Search term to filter posts and users (required)
 * @returns Published posts matching the search and users matching the search with their avatars and social media
 * @usedIn Search page layout
 */
export const SEARCH_ALL = gql`
  query searchResults($limit: Int, $skip: Int, $search: String!) {
    publishedPosts(limit: $limit, skip: $skip, search: $search) {
      postId
      title
      slug
      type
      description
      publishedAt
      modifiedAt
      image {
        sourceUrl
        sourceSet
      }
      authors {
        displayName
        preferredUsername
      }
      categories {
        name
        slug
      }
    }
    searchUsers(input: $search) {
      preferredUsername
      socialMedia {
        type
        link
      }
      avatar {
        path
        sourceUrl
        sourceSet
        metadata {
          bucket
          mimetype
          s3Url
          filename
        }
      }
    }
  }
`;

/**
 * Query to fetch a single published post by slug with full details.
 *
 * @param slug - The post slug identifier (required)
 * @returns Complete post data including content, images, authors, credits, categories, tags, and submission info
 * @usedIn Post page layout and getStaticProps
 */
export const POST = gql`
  query post($slug: String!) {
    publishedPost(slug: $slug) {
      postId
      title
      slug
      type
      status
      description
      content
      imageId
      image {
        sourceUrl
        sourceSet
      }
      featured {
        position
      }
      modifiedAt
      publishedAt
      createdAt
      updatedAt
      deletedAt
      authors {
        displayName
        preferredUsername
        role
        avatar {
          mediaId
          sourceUrl
          sourceSet
        }
        socialMedia {
          type
          link
        }
      }
      submission {
        sponsoredBy
      }
      credits {
        creditId
        creditType {
          typeName
        }
        name
        user {
          userId
          avatar {
            sourceUrl
            sourceSet
          }
          preferredUsername
        }
        socialMedia {
          type
          link
        }
      }
      categories {
        name
        slug
      }
      tags {
        name
        slug
      }
    }
  }
`;

/**
 * Query to fetch a post by slug for preview (includes unpublished posts).
 *
 * @param slug - The post slug identifier (optional)
 * @returns Complete post data including content, images, authors, credits, categories, and tags
 * @usedIn Preview page for unpublished/draft posts
 */
export const PREVIEW_POST = gql`
  query post($slug: String) {
    post(slug: $slug) {
      postId
      title
      slug
      type
      status
      description
      content
      imageId
      image {
        sourceUrl
        sourceSet
      }
      featured {
        position
      }
      modifiedAt
      publishedAt
      createdAt
      updatedAt
      deletedAt
      authors {
        displayName
        preferredUsername
        role
        avatar {
          mediaId
          sourceUrl
          sourceSet
        }
        socialMedia {
          type
          link
        }
      }
      submission {
        sponsoredBy
      }
      credits {
        creditId
        creditType {
          typeName
        }
        name
        user {
          userId
          avatar {
            sourceUrl
            sourceSet
          }
          preferredUsername
        }
        socialMedia {
          type
          link
        }
      }
      categories {
        name
        slug
      }
      tags {
        name
        slug
      }
    }
  }
`;

/**
 * Query to fetch popular published posts with pagination.
 *
 * @param limit - Number of posts to fetch (required)
 * @param skip - Number of posts to skip for pagination (required)
 * @returns List of popular published posts with basic info (title, slug, authors, image, categories)
 * @usedIn PopularPostList organism
 */
export const POPULAR_POSTS = gql`
  query popularPosts($limit: Int!, $skip: Int!) {
    publishedPosts(limit: $limit, skip: $skip) {
      postId
      title
      slug
      type
      modifiedAt
      publishedAt
      authors {
        userId
        preferredUsername
      }
      image {
        sourceUrl
      }
      categories {
        name
        slug
      }
    }
  }
`;

/**
 * Query to fetch published posts filtered by post type.
 *
 * @param limit - Maximum number of posts to return (optional)
 * @param skip - Number of posts to skip for pagination (optional)
 * @param type - The post type to filter by (required, e.g., 'article', 'video', etc.)
 * @returns List of published posts of the specified type with images, authors, and categories
 * @usedIn PostType page layout
 */
export const POSTS_BY_TYPE = gql`
  query postsByType($limit: Int, $skip: Int, $type: PostType!) {
    publishedPosts(limit: $limit, skip: $skip, type: $type) {
      postId
      title
      slug
      type
      description
      publishedAt
      modifiedAt
      image {
        sourceUrl
        sourceSet
      }
      authors {
        displayName
        preferredUsername
      }
      categories {
        name
        slug
      }
    }
  }
`;

/**
 * Query to fetch published posts authored by a specific user.
 *
 * @param preferredUsername - The user's preferred username (required)
 * @param limit - Maximum number of posts to return (optional)
 * @param skip - Number of posts to skip for pagination (optional)
 * @returns List of published posts authored by the user with basic info (title, slug, authors, image, categories)
 * @usedIn User profile page (ProfilePosts)
 */
export const POSTS_BY_USER = gql`
  query postsByUser($preferredUsername: String!, $limit: Int, $skip: Int) {
    posts: postsByUser(
      preferredUsername: $preferredUsername
      limit: $limit
      skip: $skip
    ) {
      postId
      title
      slug
      modifiedAt
      publishedAt
      authors {
        preferredUsername
        displayName
      }
      image {
        sourceUrl
      }
      categories {
        name
        slug
      }
    }
  }
`;

/**
 * Query to fetch published posts that mention or are about a specific user.
 *
 * @param preferredUsername - The user's preferred username (required)
 * @param limit - Maximum number of posts to return (optional)
 * @param skip - Number of posts to skip for pagination (optional)
 * @returns List of published posts about the user with basic info (title, slug, authors, image, categories)
 * @usedIn User profile page (ProfilePosts)
 */
export const POSTS_ABOUT_USER = gql`
  query postsAboutUser($preferredUsername: String!, $limit: Int, $skip: Int) {
    posts: postsAboutUser(
      preferredUsername: $preferredUsername
      limit: $limit
      skip: $skip
    ) {
      postId
      title
      slug
      modifiedAt
      publishedAt
      authors {
        userId
        preferredUsername
      }
      image {
        sourceUrl
      }
      categories {
        name
        slug
      }
    }
  }
`;
